import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const CartContext = createContext(null);
const KEY = "sky_cart_v2";

const clienteVazio = {
  nome: "", cnpj: "", contato: "", observacoes: "",
  condicao_pagamento: "", prazo_entrega: "",
};

const carregar = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const CartProvider = ({ children }) => {
  const inicial = carregar() || {};
  const [uf, setUf] = useState(inicial.uf || "SP");
  const [enquadramento, setEnquadramento] = useState(inicial.enquadramento || "REVENDA");
  // itens: [{produto_id, codigo, descricao_produto, unidade, ncm, desconto_maximo, quantidade, desconto}]
  // "desconto" é SEMPRE por item (fração), negociado no orçamento.
  const [itens, setItens] = useState(inicial.itens || []);
  const [cliente, setCliente] = useState(inicial.cliente || clienteVazio);
  // quando não-nulo, o orçamento em edição é um orçamento JÁ SALVO (id/número) —
  // "Salvar" passa a atualizar (PUT) este orçamento em vez de criar um novo.
  const [editandoId, setEditandoId] = useState(inicial.editandoId || null);
  const [editandoNumero, setEditandoNumero] = useState(inicial.editandoNumero || null);
  const [calculo, setCalculo] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [erroCalculo, setErroCalculo] = useState("");

  useEffect(() => {
    localStorage.setItem(
      KEY,
      JSON.stringify({ uf, enquadramento, itens, cliente, editandoId, editandoNumero })
    );
  }, [uf, enquadramento, itens, cliente, editandoId, editandoNumero]);

  const recalcular = useCallback(async () => {
    if (!itens.length) {
      setCalculo(null);
      setErroCalculo("");
      return;
    }
    setCalculando(true);
    try {
      const r = await api.post("/calc", {
        uf,
        enquadramento,
        desconto: 0,
        itens: itens.map((i) => ({
          produto_id: i.produto_id,
          quantidade: Number(i.quantidade) || 1,
          desconto: Number(i.desconto) || 0,
        })),
      });
      setCalculo(r.data);
      setErroCalculo("");
    } catch (e) {
      setCalculo(null);
      setErroCalculo(e?.response?.data?.detail || "Não foi possível calcular o orçamento.");
    } finally {
      setCalculando(false);
    }
  }, [itens, uf, enquadramento]);

  useEffect(() => {
    recalcular();
  }, [recalcular]);

  const adicionar = useCallback((produto, quantidade = 1, desconto = 0) => {
    setItens((prev) => {
      const idx = prev.findIndex((i) => i.produto_id === produto.id);
      if (idx >= 0) {
        const copia = [...prev];
        copia[idx] = {
          ...copia[idx],
          quantidade: Number(copia[idx].quantidade) + Number(quantidade),
          desconto: desconto > 0 ? desconto : copia[idx].desconto,
        };
        return copia;
      }
      const max = Number(produto.desconto_maximo) || 0;
      return [
        ...prev,
        {
          produto_id: produto.id,
          codigo: produto.codigo,
          descricao_produto: produto.descricao_produto,
          unidade: produto.unidade,
          ncm: produto.ncm,
          desconto_maximo: max,
          quantidade: Number(quantidade) || 1,
          desconto: Math.min(Number(desconto) || 0, max),
        },
      ];
    });
  }, []);

  const atualizarItem = useCallback((produto_id, patch) => {
    setItens((prev) =>
      prev.map((i) => {
        if (i.produto_id !== produto_id) return i;
        const novo = { ...i, ...patch };
        if (patch.desconto !== undefined) {
          const max = Number(i.desconto_maximo) || 0;
          novo.desconto = Math.min(Math.max(Number(patch.desconto) || 0, 0), max);
        }
        return novo;
      })
    );
  }, []);

  /** aplica o mesmo desconto a todos os itens, respeitando o máximo de cada um */
  const aplicarDescontoEmTodos = useCallback((fracao) => {
    setItens((prev) =>
      prev.map((i) => ({
        ...i,
        desconto: Math.min(Math.max(Number(fracao) || 0, 0), Number(i.desconto_maximo) || 0),
      }))
    );
  }, []);

  const remover = useCallback((produto_id) => {
    setItens((prev) => prev.filter((i) => i.produto_id !== produto_id));
  }, []);

  const limpar = useCallback(() => {
    setItens([]);
    setCalculo(null);
  }, []);

  const carregarDeOrcamento = useCallback((q, opts = {}) => {
    setUf(q.uf);
    setEnquadramento(q.enquadramento);
    setItens(
      (q.itens || []).map((i) => ({
        produto_id: i.produto_id,
        codigo: i.codigo,
        descricao_produto: i.descricao_produto,
        unidade: i.unidade,
        ncm: i.ncm,
        desconto_maximo: i.desconto_maximo,
        quantidade: i.quantidade,
        desconto: Number(i.desconto_pct) || 0,
      }))
    );
    setCliente({
      nome: q.cliente_nome || "",
      cnpj: q.cliente_cnpj || "",
      contato: q.cliente_contato || "",
      observacoes: q.observacoes || "",
      condicao_pagamento: q.condicao_pagamento || "",
      prazo_entrega: q.prazo_entrega || "",
    });
    if (opts.editar) {
      setEditandoId(q.id);
      setEditandoNumero(q.numero);
    } else {
      setEditandoId(null);
      setEditandoNumero(null);
    }
  }, []);

  /** encerra o modo de edição e limpa o orçamento em andamento */
  const sairModoEdicao = useCallback(() => {
    setEditandoId(null);
    setEditandoNumero(null);
    setItens([]);
    setCalculo(null);
    setCliente(clienteVazio);
  }, []);

  const value = useMemo(
    () => ({
      uf,
      setUf,
      enquadramento,
      setEnquadramento,
      itens,
      adicionar,
      atualizarItem,
      aplicarDescontoEmTodos,
      remover,
      limpar,
      calculo,
      calculando,
      erroCalculo,
      recalcular,
      carregarDeOrcamento,
      cliente,
      setCliente,
      editandoId,
      editandoNumero,
      sairModoEdicao,
      quantidadeItens: itens.length,
    }),
    [
      uf,
      enquadramento,
      itens,
      adicionar,
      atualizarItem,
      aplicarDescontoEmTodos,
      remover,
      limpar,
      calculo,
      calculando,
      erroCalculo,
      recalcular,
      carregarDeOrcamento,
      cliente,
      editandoId,
      editandoNumero,
      sairModoEdicao,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart precisa estar dentro de CartProvider");
  return ctx;
};
